import { FC } from 'react';
import Script from 'next/script';
import { DoctorRootStyle } from 'styles/GlobalStyles';
import {
  AppHeader,
  ErrorBoundary,
  Footer,
  GlobalErrorFallback,
  GlobalHeader,
  MessageBar,
} from 'components';
import { LoadingBar } from 'components/core';
import { LayoutProps, Styled } from './';
import {
  useDoctorProfile,
  useFetchFavouriteProductIds,
  useMenus,
  useRetriveCart,
} from 'reactQuery/queries';
import { useRouter } from 'next/router';
import { I18nProvider } from 'contexts/i18n';
import getConfig from 'next/config';
import GlobalMaintenance from '../GlobalMaintenance/GlobalMaintenance';
import {
  isDoctorNeedToRedirect,
  isSiteUnderMaintainance,
  redirectURL,
} from './Layout.helper';
import FooterVietnam from 'components/VietnamFooter/VietnamFooter';
import { useCheckIsIDSStoreDisabled } from 'reactQuery/queries';
import { IDSStoreDisabledMessage } from 'components/IDSStoreDisabledMessage';

export const DoctorLayout: FC<LayoutProps> = ({ children }) => {
  console.log('Layout component rendered');
  const {
    data: doctorProfile,
    isLoading,
    isIdle,
    isError,
  } = useDoctorProfile();

  const { data: storeAccess } = useCheckIsIDSStoreDisabled();

  useMenus();
  useRetriveCart();
  useFetchFavouriteProductIds();

  const router = useRouter();

  const {
    publicRuntimeConfig: {
      MAINTAINANCE_START_DATE: startDate,
      MAINTAINANCE_END_DATE: endDate,
      MAINTAINANCE_COUNTRY_LIST: countryList,
      APP_HOST: appHost,
    },
  } = getConfig();

  const isVietnam = doctorProfile?.country.toUpperCase() === 'VN';

  if (isLoading || isIdle) {
    return <LoadingBar />;
  }
  if (isError) {
    router.push('/login');
    return <></>;
  }

  if (isDoctorNeedToRedirect(doctorProfile?.country)) {
    router.push(`${redirectURL(doctorProfile?.country)}`);
    return <></>;
  }

  if (
    isSiteUnderMaintainance(
      startDate,
      endDate,
      doctorProfile.country,
      countryList
    )
  ) {
    return (
      <I18nProvider locale={doctorProfile.locale}>
        <Styled.StickyHeader>
          <GlobalHeader
            tabs={doctorProfile.tabs}
            profileLinks={doctorProfile.links}
            doctorName={doctorProfile.displayName}
            pcUserName={doctorProfile.pcUserName}
            impersonator={doctorProfile.impersonator}
          />
        </Styled.StickyHeader>
        <GlobalMaintenance />
        {isVietnam ? <FooterVietnam /> : <Footer />}
      </I18nProvider>
    );
  }

  if (storeAccess?.isDisabled) {
    return (
      <I18nProvider locale={doctorProfile.locale}>
        <Styled.StickyHeader>
          <GlobalHeader
            tabs={doctorProfile.tabs}
            profileLinks={doctorProfile.links}
            doctorName={doctorProfile.displayName}
            pcUserName={doctorProfile.pcUserName}
            impersonator={doctorProfile.impersonator}
          />
        </Styled.StickyHeader>
        <IDSStoreDisabledMessage reason={storeAccess.disabledReason} />
        {isVietnam ? <FooterVietnam /> : <Footer />}
      </I18nProvider>
    );
  }

  return (
    <>
      <DoctorRootStyle />
      <Script id="pendo-analytics" strategy="afterInteractive">
        {`
          (function(apiKey){
              (function(p,e,n,d,o){var v,w,x,y,z;o=p[d]=p[d]||{};o._q=o._q||[];
              v=['initialize','identify','updateOptions','pageLoad','track'];for(w=0,x=v.length;w<x;++w)(function(m){
                  o[m]=o[m]||function(){o._q[m===v[0]?'unshift':'push']([m].concat([].slice.call(arguments,0)));};})(v[w]);
                  y=e.createElement(n);y.async=!0;y.src='https://cdn.pendo.io/agent/static/'+apiKey+'/pendo.js';
                  z=e.getElementsByTagName(n)[0];z.parentNode.insertBefore(y,z);})(window,document,'script','pendo');

                  // This function creates visitors and accounts in Pendo
                  // You will need to replace <visitor-id-goes-here> and <account-id-goes-here> with values you use in your app
                  // Please use Strings, Numbers, or Bools for value types.
                  
                    pendo.initialize({
                      visitor: {
                          id:           "${doctorProfile?.did}",
                          full_name:    "${doctorProfile?.firstName} ${doctorProfile?.lastName}",
                          role:         "${doctorProfile?.role}"
                      },                     
                    });
                  
                 
          })('ca537912-fd4a-46be-50fc-9070dad9029e');
        `}
      </Script>

      <I18nProvider locale={doctorProfile.locale}>
        <Styled.StickyHeader>
          <GlobalHeader
            tabs={doctorProfile.tabs}
            profileLinks={doctorProfile.links}
            doctorName={doctorProfile.displayName}
            pcUserName={doctorProfile.pcUserName}
            impersonator={doctorProfile.impersonator}
          />
          <MessageBar />
          <AppHeader name="" />
        </Styled.StickyHeader>
        <ErrorBoundary FallbackComponent={GlobalErrorFallback}>
          {children}
        </ErrorBoundary>
        {isVietnam ? <FooterVietnam /> : <Footer />}
      </I18nProvider>
    </>
  );
};
